<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    class="inline-flex items-center justify-center font-bold transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
    :class="[
      variantClasses[variant],
      sizeClasses[size],
      block ? 'w-full' : '',
      roundedClass
    ]"
    @click="$emit('click', $event)"
  >
    <!-- Loading Spinner -->
    <svg 
      v-if="loading" 
      class="animate-spin -ml-1 mr-2 h-4 w-4" 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>

    <!-- Icon Start -->
    <slot name="icon-left"></slot>
    
    <!-- Content -->
    <slot></slot>

    <!-- Icon End -->
    <slot name="icon-right"></slot>
  </button>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'outline', 'ghost', 'danger', 'white'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
  },
  type: {
    type: String,
    default: 'button'
  },
  block: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  rounded: {
    type: String,
    default: 'xl' // Default to xl for that modern look
  }
});

defineEmits(['click']);

const variantClasses = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20 focus:ring-blue-500 border border-transparent',
  secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-900 focus:ring-slate-500 border border-transparent dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white',
  outline: 'bg-transparent border-2 border-slate-200 hover:border-slate-300 text-slate-700 hover:text-slate-900 focus:ring-slate-400 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white dark:hover:border-slate-600',
  ghost: 'bg-transparent hover:bg-slate-100 text-slate-600 hover:text-slate-900 focus:ring-slate-400 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800',
  danger: 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20 focus:ring-red-500 border border-transparent',
  white: 'bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 shadow-sm focus:ring-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:hover:bg-slate-700'
};

const sizeClasses = {
  xs: 'px-2.5 py-1.5 text-xs',
  sm: 'px-3 py-2 text-sm leading-4',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
  xl: 'px-8 py-3.5 text-lg'
};

const roundedClass = computed(() => `rounded-${props.rounded}`);
</script>
